import { useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import AvatarUploader from '../../../../UI/AvatarUploader'
import Confetti from '../../../../UI/Confetti'

import http from '../../../../shared/http'
import { TApi, TRoles } from '../../../../shared/const'
import { errorText } from '../../../../shared/errorMessage'
import { useAvatarUrl } from '../../../../shared/useAvatarUrls'

import { IconCheck } from './icons'
import styles from './style.module.scss'

/*
 * Die Praxisaufgabe „Profilbild" — hier erledigt, nicht anderswo.
 *
 * Vorher stand auf der Folie ein Verweis ins Profil und ein Knopf
 * „Nachsehen": Wer die Aufgabe machen wollte, verließ die Schulung, lud in
 * einem zweiten Tab ein Bild hoch und kam zurück. Drei Schritte für eine
 * Sache, die einer ist — und der wahrscheinlichste Ausgang war, dass man beim
 * zweiten Schritt etwas anderes zu tun hatte.
 *
 * Jetzt steht die Karte hier, mit den eigenen Angaben und dem eigenen Bild.
 * Wer noch keines hat, sieht seinen grauen Platzhalter — und genau daneben das
 * Feld, mit dem er verschwindet. Danach fällt Konfetti und die Karte zeigt das
 * Ergebnis, statt nur „gespeichert" zu melden.
 *
 * **Freiwillig bleibt es trotzdem.** Der Satz darunter sagt, dass es auch
 * später im eigenen Profil geht; die Schulung läuft ohne das Bild genauso zu
 * Ende. Eine Aufgabe, die den Weg versperrt, ist keine Aufgabe mehr.
 */

/** Die Route zum Hochladen hängt an der Rolle. */
const AVATAR_ROUTE = {
	[TRoles.ADMIN]: TApi.ADMIN_AVATAR,
	[TRoles.FEDERAL_ADMIN]: TApi.ADMIN_AVATAR
}

export default function AvatarTask({ aufgabe, erledigt, onErledigt }) {
	const profil = useSelector(state => state.user.profile)
	const rolle = useSelector(state => state.user.role)

	const [laedt, setLaedt] = useState(false)
	// Zählt hoch statt umzuschalten: Ein gleichbleibendes `true` löst den
	// Konfettiregen kein zweites Mal aus.
	const [feier, setFeier] = useState(0)

	const bildUrl = useAvatarUrl(profil?.avatarHashName)
	const name = [profil?.firstName, profil?.lastName].filter(Boolean).join(' ').trim()
	const hatBild = Boolean(profil?.avatarHashName)

	const hochladen = async datei => {
		const route = AVATAR_ROUTE[rolle]
		if (!route) return

		setLaedt(true)
		try {
			const form = new FormData()
			form.append('avatar', datei)
			await http.put(route, form, { disableToast: true })

			/*
			 * Das frisch geladene Profil kommt in den Speicher — die Karte hier,
			 * die Abschlusskarte und die Navigationsleiste zeigen das neue Bild
			 * damit sofort und nicht erst nach dem nächsten Seitenwechsel.
			 */
			await onErledigt()
			setFeier(n => n + 1)
		} catch (error) {
			toast.error(errorText(error, 'Das Bild ließ sich gerade nicht speichern.'))
		} finally {
			setLaedt(false)
		}
	}

	return (
		<div className={styles.avatarTask}>
			<Confetti ausloeser={feier} />

			<div className={clsx(styles.avatarCard, hatBild && styles.avatarCardDone)}>
				<AvatarUploader
					src={bildUrl}
					name={name || profil?.email || '?'}
					size={92}
					disabled={laedt}
					onSelect={hochladen}
				/>

				<div className={styles.avatarIdentity}>
					<p className={styles.avatarName}>{name || profil?.email || '—'}</p>
					{profil?.email && <p className={styles.avatarMail}>{profil.email}</p>}
					<p className={styles.avatarRole}>
						{rolle === TRoles.FEDERAL_ADMIN ? 'Bundesadministration' : 'Landesadministration'}
						{profil?.state ? ` · ${profil.state}` : ''}
					</p>
				</div>

				{hatBild && (
					<span className={styles.avatarBadge}>
						<IconCheck className={styles.avatarBadgeIcon} />
						{erledigt ? 'Erledigt' : 'Steht'}
					</span>
				)}
			</div>

			{hatBild ? (
				<p className={styles.avatarHint}>
					<b>Sieht gut aus.</b> Genau dieses Bild steht ab jetzt an euren Kommentaren, Zuweisungen und
					Verlaufseinträgen — und auf eurer Abschlusskarte am Ende des Onboardings. Ändern könnt ihr es
					jederzeit im eigenen Profil.
				</p>
			) : (
				<p className={styles.avatarHint}>
					<b>Euer grauer Platzhalter hätte gern euer Gesicht.</b> Ein Klick auf den Kreis, Bild auswählen,
					zuschneiden oder drehen, fertig — es geht direkt von hier aus. Wer lieber ein anderes Foto sucht,
					macht es später im eigenen Profil; das Onboarding läuft auch ohne weiter.
				</p>
			)}

			{laedt && <p className={styles.avatarHint}>Einen Moment, das Bild wird gespeichert …</p>}
		</div>
	)
}

AvatarTask.propTypes = {
	aufgabe: PropTypes.object.isRequired,
	erledigt: PropTypes.bool,
	/** Lädt das Profil neu und schreibt die Aufgabe im Onboarding-Stand mit. */
	onErledigt: PropTypes.func.isRequired
}
